import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  TextField,
  Button,
  Tooltip,
  LinearProgress,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  Collections as CollectionsIcon,
} from "@mui/icons-material";
import {
  uploadProductImage,
  validateImageFile,
} from "../../services/uploadService";
import { getStaticFileUrl } from "../../config/apiConfig";
import { ImageItem } from "../../types/upload";
import MediaLibraryModal from "./MediaLibraryModal";
import { MediaFile } from "../../types/media";

interface ImageUploadProps {
  images: ImageItem[];
  onChange: (images: ImageItem[]) => void;
  maxImages?: number;
  maxSizeMB?: number;
  disabled?: boolean;
  showMainToggle?: boolean;
}

// Use crypto.randomUUID() for better ID generation (no collisions)
const generateId = () => crypto.randomUUID();

// Enforce "exactly one main" invariant
const normalizeMainImage = (imgs: ImageItem[]): ImageItem[] => {
  if (imgs.length === 0) return imgs;

  const hasMain = imgs.some(img => img.isMain);

  if (!hasMain) {
    // No main - set first as main
    return imgs.map((img, idx) => ({ ...img, isMain: idx === 0 }));
  }

  // If multiple mains, keep only first
  let foundMain = false;
  return imgs.map(img => {
    if (img.isMain && !foundMain) {
      foundMain = true;
      return img;
    }
    return { ...img, isMain: false };
  });
};

// Helper to resolve image src (handles both external URLs and server paths)
const resolveImageSrc = (url: string): string => {
  try {
    // Check if it's an absolute URL
    new URL(url);
    return url; // External URL or blob URL
  } catch {
    // Relative server path
    return getStaticFileUrl(url);
  }
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onChange,
  maxImages = 10,
  maxSizeMB = 5,
  disabled = false,
  showMainToggle = true,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [urlInputOpen, setUrlInputOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // Track current images for async upload callbacks
  const imagesRef = useRef<ImageItem[]>(images);
  imagesRef.current = images;

  // AbortController map for upload cancellation
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Memoize canAddMore
  const canAddMore = useMemo(() => images.length < maxImages, [images.length, maxImages]);

  // Cleanup blob URLs on unmount only (not on every images change)
  useEffect(() => {
    return () => {
      imagesRef.current
        .filter(img => img.objectUrl?.startsWith("blob:"))
        .forEach(img => URL.revokeObjectURL(img.objectUrl!));
    };
  }, []);

  // Cleanup drag state and abort controllers on unmount
  useEffect(() => {
    return () => {
      dragCounter.current = 0;
      setIsDragging(false);

      // Abort all ongoing uploads
      uploadControllersRef.current.forEach(controller => controller.abort());
      uploadControllersRef.current.clear();
    };
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || !canAddMore) return;

    if (!e.dataTransfer.types.includes("Files")) return;

    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, [disabled, canAddMore]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Helper to update a specific image by id
  const updateImage = useCallback(
    (imageId: string, updates: Partial<ImageItem>) => {
      const imageExists = imagesRef.current.some(img => img.id === imageId);
      if (!imageExists) return;

      const updatedImages = imagesRef.current.map((img) =>
        img.id === imageId ? { ...img, ...updates } : img
      );
      onChange(normalizeMainImage(updatedImages));
    },
    [onChange]
  );

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const currentImages = imagesRef.current;
      const availableSlots = maxImages - currentImages.length;

      if (availableSlots <= 0) {
        return;
      }

      const filesToProcess = fileArray.slice(0, availableSlots);

      // Create placeholder images with uploading state
      const newImages: ImageItem[] = filesToProcess.map((file, index) => {
        const objectUrl = URL.createObjectURL(file);
        return {
          id: generateId(),
          url: objectUrl,
          objectUrl: objectUrl,
          isMain: currentImages.length === 0 && index === 0,
          isUploading: true,
          uploadProgress: 0,
        };
      });

      const updatedImages = normalizeMainImage([...currentImages, ...newImages]);
      onChange(updatedImages);

      // Upload each file
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        const imageId = newImages[i].id;
        const objectUrl = newImages[i].objectUrl!;

        const abortController = new AbortController();
        uploadControllersRef.current.set(imageId, abortController);

        const validation = validateImageFile(file, maxSizeMB);
        if (!validation.isValid) {
          URL.revokeObjectURL(objectUrl);

          const remaining = imagesRef.current.filter((img) => img.id !== imageId);
          onChange(normalizeMainImage(remaining));

          uploadControllersRef.current.delete(imageId);
          continue;
        }

        try {
          const result = await uploadProductImage(
            file,
            (progress) => {
              updateImage(imageId, { uploadProgress: progress });
            },
            { signal: abortController.signal }
          );

          if (abortController.signal.aborted) {
            continue;
          }

          URL.revokeObjectURL(objectUrl);

          updateImage(imageId, {
            url: result.url,
            objectUrl: undefined,
            isUploading: false,
            uploadProgress: 100,
            error: undefined,
          });
        } catch (error: any) {
          if (abortController.signal.aborted || error?.code === "ERR_CANCELED") {
            continue;
          }

          const errorMessage =
            error instanceof Error ? error.message : "Upload failed";
          updateImage(imageId, { isUploading: false, error: errorMessage });
        } finally {
          uploadControllersRef.current.delete(imageId);
        }
      }
    },
    [maxImages, maxSizeMB, onChange, updateImage]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [disabled, processFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFiles]
  );

  const handleAddUrl = useCallback(() => {
    if (!canAddMore) {
      setUrlError(`Maximum ${maxImages} images reached`);
      return;
    }

    if (!newImageUrl.trim()) {
      setUrlError("Please enter a URL");
      return;
    }

    // Basic URL validation
    try {
      new URL(newImageUrl);
    } catch {
      setUrlError("Please enter a valid URL");
      return;
    }

    const currentImages = imagesRef.current;
    const newImage: ImageItem = {
      id: generateId(),
      url: newImageUrl.trim(),
      isMain: currentImages.length === 0,
    };

    onChange(normalizeMainImage([...currentImages, newImage]));
    setNewImageUrl("");
    setUrlError(null);
    setUrlInputOpen(false);
  }, [newImageUrl, canAddMore, maxImages, onChange]);

  const handleRemoveImage = useCallback(
    (id: string) => {
      const controller = uploadControllersRef.current.get(id);
      if (controller) {
        controller.abort();
        uploadControllersRef.current.delete(id);
      }

      const currentImages = imagesRef.current;
      const imageToRemove = currentImages.find((img) => img.id === id);

      if (imageToRemove?.objectUrl) {
        URL.revokeObjectURL(imageToRemove.objectUrl);
      }

      const newImages = currentImages.filter((img) => img.id !== id);
      onChange(normalizeMainImage(newImages));
    },
    [onChange]
  );

  const handleSetMainImage = useCallback(
    (id: string) => {
      const currentImages = imagesRef.current;
      const newImages = currentImages.map((img) => ({
        ...img,
        isMain: img.id === id,
      }));
      onChange(normalizeMainImage(newImages));
    },
    [onChange]
  );

  const handleRetryUpload = useCallback(
    (id: string) => {
      handleRemoveImage(id);
    },
    [handleRemoveImage]
  );

  const handleSelectFromLibrary = useCallback(
    (selectedMedia: MediaFile[]) => {
      const currentImages = imagesRef.current;
      const availableSlots = maxImages - currentImages.length;

      if (availableSlots <= 0) return;

      const mediaToAdd = selectedMedia.slice(0, availableSlots);
      const newImages: ImageItem[] = mediaToAdd.map((media, index) => ({
        id: generateId(),
        url: media.relativePath,
        isMain: currentImages.length === 0 && index === 0,
      }));

      onChange(normalizeMainImage([...currentImages, ...newImages]));
    },
    [maxImages, onChange]
  );

  const handleDropZoneKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled || !canAddMore) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [disabled, canAddMore]);

  return (
    <Box>
      {/* Drop Zone */}
      <Paper
        role="button"
        tabIndex={disabled || !canAddMore ? -1 : 0}
        aria-label="Upload images by clicking or drag and drop"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && canAddMore && fileInputRef.current?.click()}
        onKeyDown={handleDropZoneKeyDown}
        sx={{
          p: 3,
          border: "2px dashed",
          borderColor: isDragging
            ? "primary.main"
            : disabled
              ? "grey.300"
              : "grey.400",
          borderRadius: 2,
          bgcolor: isDragging
            ? alpha("#1976d2", 0.05)
            : disabled
              ? "grey.50"
              : "background.paper",
          cursor: disabled || !canAddMore ? "default" : "pointer",
          transition: "all 0.2s ease",
          textAlign: "center",
          "&:hover": {
            borderColor:
              disabled || !canAddMore ? undefined : "primary.main",
            bgcolor:
              disabled || !canAddMore ? undefined : alpha("#1976d2", 0.02),
          },
          "&:focus-visible": {
            outline: `2px solid ${alpha("#1976d2", 0.5)}`,
            outlineOffset: 2,
          },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={disabled || !canAddMore}
          aria-label="File input for image upload"
        />

        <UploadIcon
          sx={{
            fontSize: 48,
            color: isDragging ? "primary.main" : "grey.400",
            mb: 1,
          }}
        />

        <Typography variant="body1" color="text.secondary" gutterBottom>
          {isDragging
            ? t("admin.imageUpload.dropHere")
            : canAddMore
              ? t("admin.imageUpload.dragAndDrop")
              : t("admin.imageUpload.maxReached", { max: maxImages })}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {t("admin.imageUpload.supportedFormats", { maxSize: maxSizeMB })}
        </Typography>

        {canAddMore && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoIcon />}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={disabled}
            >
              {t("admin.imageUpload.uploadFiles")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CollectionsIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setMediaLibraryOpen(true);
              }}
              disabled={disabled}
            >
              {t("admin.imageUpload.browseLibrary")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setUrlInputOpen(true);
              }}
              disabled={disabled}
            >
              {t("admin.imageUpload.addUrl")}
            </Button>
          </Box>
        )}
      </Paper>

      {/* URL Input Dialog */}
      {urlInputOpen && (
        <Paper sx={{ mt: 2, p: 2 }} variant="outlined">
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={t("admin.imageUpload.imageUrl")}
              placeholder="https://example.com/image.jpg"
              value={newImageUrl}
              onChange={(e) => {
                setNewImageUrl(e.target.value);
                setUrlError(null);
              }}
              error={!!urlError}
              helperText={urlError}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddUrl();
                }
              }}
              aria-label="Image URL input"
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAddUrl}
              sx={{ minWidth: 80 }}
            >
              {t("common.add")}
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setUrlInputOpen(false);
                setNewImageUrl("");
                setUrlError(null);
              }}
              aria-label="Close URL input"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {images.map((image, index) => (
            <Grid item xs={6} sm={4} md={3} key={image.id}>
              <Paper
                variant="outlined"
                sx={{
                  position: "relative",
                  aspectRatio: "1/1",
                  overflow: "hidden",
                  borderRadius: 1,
                  border: image.isMain && showMainToggle ? "2px solid" : "1px solid",
                  borderColor: image.isMain && showMainToggle ? "primary.main" : "divider",
                }}
              >
                {/* Image */}
                <Box
                  component="img"
                  src={resolveImageSrc(image.url)}
                  alt={`Image ${index + 1}${image.isMain ? ' (main)' : ''}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: image.isUploading ? 0.5 : 1,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/200?text=Error";
                  }}
                />

                {/* Upload Progress */}
                {image.isUploading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.8)",
                    }}
                    aria-live="polite"
                    aria-label={`Uploading: ${image.uploadProgress}%`}
                  >
                    <CircularProgress size={32} />
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {image.uploadProgress}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={image.uploadProgress || 0}
                      sx={{ width: "80%", mt: 1 }}
                    />
                  </Box>
                )}

                {/* Error State */}
                {image.error && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,0,0,0.1)",
                      p: 1,
                    }}
                  >
                    <Alert severity="error" sx={{ fontSize: "10px", mb: 1 }}>
                      {image.error}
                    </Alert>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRetryUpload(image.id)}
                      sx={{ fontSize: "10px" }}
                    >
                      {t("common.remove")}
                    </Button>
                  </Box>
                )}

                {/* Main Badge */}
                {showMainToggle && image.isMain && !image.isUploading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      bgcolor: "primary.main",
                      color: "white",
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: "10px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <StarIcon sx={{ fontSize: 12 }} />
                    MAIN
                  </Box>
                )}

                {/* Actions */}
                {!image.isUploading && !disabled && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      display: "flex",
                      gap: 0.5,
                    }}
                  >
                    {showMainToggle && !image.isMain && (
                      <Tooltip title="Set as main image">
                        <IconButton
                          size="small"
                          onClick={() => handleSetMainImage(image.id)}
                          aria-label="Set as main image"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.9)",
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove image">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(image.id)}
                        aria-label="Remove image"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.9)",
                          "&:hover": { bgcolor: "white", color: "error.main" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Image Number */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "white",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: "11px",
                  }}
                >
                  {index + 1} / {images.length}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Image Count Info */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {t("admin.imageUpload.imageCount", { count: images.length, max: maxImages })}
        {showMainToggle && images.length > 0 && ` \u2022 ${t("admin.imageUpload.clickStarHint")}`}
      </Typography>

      {/* Media Library Modal */}
      <MediaLibraryModal
        open={mediaLibraryOpen}
        onClose={() => setMediaLibraryOpen(false)}
        onSelect={handleSelectFromLibrary}
        multiSelect={true}
        maxSelect={maxImages - images.length}
        mediaType="Product"
      />
    </Box>
  );
};

export default ImageUpload;
